<script>
import { cloneDeep } from 'lodash-es';
import { mapState } from 'vuex';
import ValidatorMixin from '../../mixins/validatorMixin';
import TwCopyAndTranslateModal from '../TwCopyAndTranslateModal';

export default {
  name: 'ItineraryIncludeList',
  components: { TwCopyAndTranslateModal },
  mixins: [ ValidatorMixin ],
  props: {
    itineraryId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    includeItems: {
      type: Array,
      default: () => [],
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
    includeCategories: {
      type: Array,
      default: () => [],
    },
    listTitle: {
      type: String,
      default: '',
    },
    includeNot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localIncludeItems: [],
      headers: [
        { text: this.$t('itinerary.edit.include.table.no'), sortable: false, width: '5' },
        { text: this.$t('itinerary.edit.include.table.category'), sortable: false, width: '15' },
        { text: `${this.$t('itinerary.edit.include.table.headline')} & ${this.$t('itinerary.edit.include.table.text')}`, sortable: false, width: '75' },
        { text: this.$t('itinerary.edit.include.table.actions'), sortable: false, width: '5', align: 'end' },
      ],
      valid: true,
      copyAndTranslateModalOpen: false,
    };
  },
  computed: {
    ...mapState({
      domains: state => state.itinerary.domains,
    }),
    getIncludeCopyValues() {
      const values = [];
      this.includeItems.forEach((includeItem, index) => {
        values.push({
          globalModelId: includeItem.id,
          globalModelColumnName: 'itinerary_include_id',
          // We need an override for domain_id key on this since it references an itinerary_include_id in the db
          selectKeyOverrides: [ 'itinerary_include_id', 'domain_id' ],
          tableName: 'itinerary_domain_includes',
          sourceDomainId: null,
          selectedFromDomain: null,
          fromLanguageIsoOverride: null,
          sourceIsoOverride: null,
          toDomains: [],
          shouldTranslate: false,
          shouldCopySeo: false,
          fieldNamesAndValuesToCopyAndTranslate: [
            { headline: includeItem.headline },
            { text: includeItem.text },
          ],
          title: `Include: ${index + 1}`,
        });
      });
      return values;
    },
  },
  watch: {
    includeItems() {
      this.localIncludeItems = cloneDeep(this.includeItems);
    },
  },
  created() {
    this.localIncludeItems = cloneDeep(this.includeItems);
  },

  methods: {
    save() {
      this.$emit('saveIncludes', this.localIncludeItems);
    },
    swap(a, b) {
      const i = this.localIncludeItems[a];
      this.$set(this.localIncludeItems, a, this.localIncludeItems[b]);
      this.$set(this.localIncludeItems, b, i);
    },
    moveItemUp(idx) {
      this.swap(idx, idx - 1);
    },
    moveItemDown(idx) {
      this.swap(idx, idx + 1);
    },
    sort() {
      if (this.localIncludeItems.length > 1) {
        // add includeCategories.priority for sorting
        this.includeCategories.forEach(
          item => {
            this.localIncludeItems.forEach(
              (incItem, idx) => {
                if (incItem.includeCategoryId === item.id) {
                  this.localIncludeItems[idx].includeCategoryPriority = item.priority;
                }
              },
            );
          },
        );

        let changes;
        do {
          changes = false;
          for (let i = 0; i < this.localIncludeItems.length - 1; i += 1) {
            if (this.localIncludeItems[i].includeCategoryPriority >
              this.localIncludeItems[i + 1].includeCategoryPriority) {
                this.swap(i, i + 1);
                changes = true;
            } else if (this.localIncludeItems[i].includeCategoryPriority ===
              this.localIncludeItems[i + 1].includeCategoryPriority &&
              this.localIncludeItems[i].headline > this.localIncludeItems[i + 1].headline) {
                this.swap(i, i + 1);
                changes = true;
              }
          }
        } while (changes);
      }
    },
    addItem() {
      const domains = {};

      this.domains.items.forEach(
        domain => {
          domains[domain.domainId] = { domainId: domain.domainId, headline: '', text: '' };
        },
      );

      this.localIncludeItems.push({ includeCategoryId: 1, headline: '', text: '', priority: 9999, domains });
    },
  },
};
</script>

<template>
  <v-container class="pa-0 fluid">
    <v-card-title class="pb-0">
      <div class="headline">{{ listTitle }}</div>
    </v-card-title>
    <v-layout row wrap>
      <v-flex class="my-12">
        <v-form v-model="valid">
          <v-data-table
            :items="localIncludeItems"
            :headers="headers"
            :loading="loading"
            hide-actions
            class="elevation-1 edit-include"
          >
            <template v-slot:headers="props">
              <tr>
                <th
                  v-for="(header, index) in props.headers"
                  :key="index"
                  :style="{ 'width': `${header.width}%` }"
                  class="text-xs-left"
                >
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr>
                <td>{{ $t('itinerary.edit.include.table.iterator', [props.index + 1]) }}</td>
                <td>
                  <div class="input-container">
                    <select
                      v-model="props.item.includeCategoryId"
                      class="styled-select"
                    >
                      <option
                        v-for="category in includeCategories"
                        :key="category.id"
                        :value="category.id"
                      >
                        {{ category.description }}
                      </option>
                    </select>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-col">
                    <div class="d-flex gap-3 flex-row">
                      <div class="input-container">
                        <input
                          v-model="props.item.headline"
                          type="text"
                          class="styled-input"
                        >
                      </div>

                      <div class="input-container">
                        <textarea
                          v-model="props.item.text"
                          class="styled-textarea"
                          rows="4"
                        />
                      </div>

                    </div>
                    <div
                      v-if="selectedDomain"
                      class="d-flex gap-3 flex-row"
                    >
                      <div
                        class="input-container"
                      >
                        <label class="input-label">{{ $t('itinerary.edit.include.table.localHeadline') }}</label>
                        <input
                          v-model="props.item.domains[''+selectedDomain.domainId].headline"
                          type="text"
                          class="styled-input"
                        >
                      </div>

                      <div class="input-container">
                        <label class="input-label">{{ $t('itinerary.edit.include.table.localText') }}</label>
                        <textarea
                          v-model="props.item.domains[''+selectedDomain.domainId].text"
                          class="styled-textarea"
                          rows="4"
                        />
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-xs-right">
                  <div class="d-flex flex-col gap align-end input-container">
                    <v-btn
                      :disabled="props.index == 0"
                      fab
                      small
                      depressed
                      color="primary"
                      class="ma-0"
                      @click="moveItemUp(props.index)"
                    >
                      <v-icon>arrow_upward</v-icon>
                    </v-btn>
                    <v-btn
                      :disabled="props.index == localIncludeItems.length-1"
                      fab
                      small
                      depressed
                      color="primary"
                      class="ma-0"
                      @click="moveItemDown(props.index)"
                    >
                      <v-icon>arrow_downward</v-icon>
                    </v-btn>
                    <v-btn
                      fab
                      small
                      depressed
                      color="error"
                      class="ma-0"
                      @click="localIncludeItems.splice(props.index, 1)"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-btn
            :loading="loading"
            color="primary success"
            class="mt-4"
            @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
          >
            Copy/translate
          </v-btn>
          <tw-copy-and-translate-modal
            v-if="copyAndTranslateModalOpen"
            :elements-to-handle="getIncludeCopyValues"
            :domains="domains.items.map(domain => domain.domain)"
          />
          <v-btn
            color="success"
            class="left mr-4 mt-4"
            @click="sort"
          >
            {{ $t('buttons.sort') }}
            <v-icon class="ml-1">sort</v-icon>
          </v-btn>
          <v-btn
            :loading="loading"
            color="primary"
            class="right mr-4 mt-4"
            @click="save()"
          >
            {{ $t('buttons.save') }}
            <v-icon class="ml-1">save</v-icon>
          </v-btn>
          <v-btn
            :loading="loading"
            color="success"
            class="right mr-4 mt-4"
            @click="addItem()"
          >
            {{ $t('buttons.add') }}
            <v-icon class="ml-1">add</v-icon>
          </v-btn>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables.scss';

  .gap {
    gap: 8px;
  }

  .align-end {
    align-items: flex-end;
  }

  .input-container {
    margin: 16px 0;

    .input-label {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
    }

    .styled-input {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 8px;
      background-color: #f5f5f5;
      transition: all 0.2s ease;
      font-size: 16px;
      color: rgba(0, 0, 0, 0.87);

      &:focus {
        outline: none;
        border-color: var(--v-primary-base);
        background-color: #ffffff;
        box-shadow: 0 0 0 2px rgba(var(--v-primary-base), 0.1);
      }

      &:hover {
        background-color: #ffffff;
        border-color: rgba(0, 0, 0, 0.24);
      }
    }
  }

  .styled-textarea {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background-color: #f5f5f5;
    transition: all 0.2s ease;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87);
    resize: vertical;
    min-height: 150px;
    line-height: 1.5;
    font-family: inherit;

    &:hover {
      background-color: #ffffff;
      border-color: rgba(0, 0, 0, 0.24);
    }

    &:focus {
      outline: none;
      border-color: var(--v-primary-base);
      background-color: #ffffff;
      box-shadow: 0 0 0 2px rgba(var(--v-primary-base), 0.1);
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  .styled-select {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background-color: #f5f5f5;
    transition: all 0.2s ease;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87);
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 16px center;
    background-size: 16px;
    padding-right: 40px;

    &:hover {
      background-color: #ffffff;
      border-color: rgba(0, 0, 0, 0.24);
    }

    &:focus {
      outline: none;
      border-color: var(--v-primary-base);
      background-color: #ffffff;
      box-shadow: 0 0 0 2px rgba(var(--v-primary-base), 0.1);
    }

    option {
      background-color: #ffffff;
      color: rgba(0, 0, 0, 0.87);
      padding: 8px;
    }
  }

  .expanded-row {
    position: relative;
    display: table-row;
    height: $expanded-row-height;
    width: 100%;
    overflow: hidden;
  }

  .expanded-row-input {
    margin-top: $margin-lg;
    margin-left: $margin-lg;
    width: $expanded-row-input-width;
  }

  .expanded-row-data {
    position: absolute;
    width: 100%;
    height: $expanded-row-height;
    overflow: auto;
  }

  .expanded-row-form {
    width: 50%;
    float: right;
  }

  .edit-include {
    td {
      vertical-align: top;
      padding: 16px;
    }

    .v-input {
      height: 120px;
    }

    td:first-child {
      padding-top: 15px;
    }

    tr:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  .v-btn {
    transition: transform 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryIncludeList.vue